import { useEffect, useLayoutEffect, useRef, useState, type ComponentType } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  Smartphone,
  Globe,
  LayoutGrid,
  Repeat,
  Target,
  Flame,
  Lock,
  Palette,
  Gift,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react'
import {
  FreeMock,
  ApplePayMock,
  CurrencyMock,
  CategoriesMock,
  SubscriptionMock,
  GoalsMock,
  StreakMock,
  PrivacyMock,
  ThemeMock,
} from '../components/AppMocks'

gsap.registerPlugin(ScrollTrigger)

type Feature = {
  Icon: LucideIcon
  title: string
  desc: string
  Mock: ComponentType
}

const FEATURES: Feature[] = [
  { Icon: Gift, title: 'Free, fully', desc: 'All features unlocked. No paywall, no ads.', Mock: FreeMock },
  { Icon: Smartphone, title: 'Apple Pay auto-import', desc: 'Tap to pay. Transaction lands in Ruvi.', Mock: ApplePayMock },
  { Icon: Globe, title: '35+ currencies', desc: 'Multi-currency accounts. Live rates.', Mock: CurrencyMock },
  { Icon: LayoutGrid, title: 'Custom categories', desc: 'Add, rename, recolor, nest subcategories.', Mock: CategoriesMock },
  { Icon: Repeat, title: 'Subscriptions & trials', desc: 'Renewals and free trials, tracked.', Mock: SubscriptionMock },
  { Icon: Target, title: 'Goals & daily limit', desc: 'A daily allowance that adjusts itself.', Mock: GoalsMock },
  { Icon: Flame, title: 'Streaks & XP', desc: 'Show up daily. Level up.', Mock: StreakMock },
  { Icon: Lock, title: 'Private by default', desc: 'On-device. iCloud via your account.', Mock: PrivacyMock },
  { Icon: Palette, title: 'Pick a theme', desc: 'Repaint the app to your taste.', Mock: ThemeMock },
]

const N = FEATURES.length
const GAP = 8
const RAIL_HEIGHT = 520
// Reveal content is rendered at a fixed inner width — text wraps once on
// mount and never reflows as the column animates. Picked to match the
// active-panel width minus internal padding (28px each side ≈ 56).
const REVEAL_INNER_W = 420

type Layout = { activeW: number; collapsedW: number }

function computeLayout(containerW: number): Layout {
  // Active panel: ~40% of rail, capped at 480px. Wider feels too dominant.
  const activeW = Math.max(340, Math.min(480, containerW * 0.4))
  // Remaining width split equally across N-1 collapsed panels, minus gaps.
  const collapsedTotal = containerW - activeW - GAP * (N - 1)
  const collapsedW = Math.max(56, collapsedTotal / (N - 1))
  return { activeW, collapsedW }
}

function panelPosition(i: number, active: number, layout: Layout): { x: number; w: number } {
  let x = 0
  for (let j = 0; j < i; j++) {
    x += (j === active ? layout.activeW : layout.collapsedW) + GAP
  }
  const w = i === active ? layout.activeW : layout.collapsedW
  return { x, w }
}

export function Features() {
  const rootRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLDivElement>(null)
  const panelRefs = useRef<Array<HTMLElement | null>>([])
  // Active panel as a ref — rapid mouseenter doesn't re-render React. All
  // visual state lives in the DOM, animated by a single GSAP source per panel.
  const activeRef = useRef<number>(0)
  const layoutRef = useRef<Layout | null>(null)

  // Title + entry stagger via ScrollTrigger.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const ctx = gsap.context(() => {
      gsap.from('.feat-title', {
        opacity: 0,
        y: 24,
        duration: 1,
        ease: 'expo.out',
        scrollTrigger: { trigger: root, start: 'top 75%' },
      })
      gsap.from('.feat-panel', {
        opacity: 0,
        y: 40,
        duration: 0.9,
        ease: 'expo.out',
        stagger: 0.05,
        scrollTrigger: { trigger: '.feat-rail', start: 'top 85%' },
      })
    }, root)
    return () => ctx.revert()
  }, [])

  // Apply x/width to every panel for the current activeRef state. Animated
  // = false during initial mount + on resize, true on hover changes.
  const applyLayout = (animated: boolean) => {
    const layout = layoutRef.current
    if (!layout) return
    const active = activeRef.current
    panelRefs.current.forEach((el, i) => {
      if (!el) return
      const { x, w } = panelPosition(i, active, layout)
      if (animated) {
        gsap.to(el, {
          x,
          width: w,
          duration: 0.6,
          ease: 'expo.out',
          overwrite: 'auto',
        })
      } else {
        gsap.set(el, { x, width: w })
      }
    })
  }

  // Tween only the per-panel content (icons, glow, reveal/strip cross-fade).
  // Layout (x/width) is handled by applyLayout above.
  //
  // For opacity tweens (reveal + strip + glow), we explicitly kill any
  // in-flight or *delayed* tween on that property before scheduling the new
  // one. GSAP's `overwrite: 'auto'` sometimes lets a queued-but-not-yet-
  // started delayed tween slip through, which was the "stuck open" bug
  // visible during fast mouse sweeps: the deactivation tween fired
  // immediately, then the OLD activation's delayed tween woke up at
  // t=0.24s and dragged opacity back to 1.
  const tweenContent = (i: number, isActive: boolean) => {
    const el = panelRefs.current[i]
    if (!el) return
    const ease = 'expo.out'
    const dur = 0.6

    const iconBox = el.querySelector('.panel-icon-box')
    const icon = el.querySelector('.panel-icon')
    const glow = el.querySelector('.panel-glow')
    const strip = el.querySelector('.panel-strip-title')
    const reveal = el.querySelector('.panel-reveal')

    gsap.to(iconBox, {
      scale: isActive ? 1.06 : 1,
      rotation: isActive ? 4 : 0,
      duration: dur,
      ease,
      overwrite: 'auto',
    })
    gsap.to(icon, {
      scale: isActive ? 1.12 : 1,
      rotation: isActive ? -10 : 0,
      duration: dur,
      ease,
      overwrite: 'auto',
    })
    if (glow) {
      gsap.killTweensOf(glow, 'opacity')
      gsap.to(glow, { opacity: isActive ? 1 : 0, duration: dur, ease })
    }
    if (strip) {
      gsap.killTweensOf(strip, 'opacity')
      gsap.to(strip, {
        opacity: isActive ? 0 : 1,
        duration: isActive ? 0.18 : 0.28,
        delay: isActive ? 0 : 0.2,
        ease: 'power2.out',
      })
    }
    if (reveal) {
      gsap.killTweensOf(reveal, 'opacity')
      gsap.to(reveal, {
        opacity: isActive ? 1 : 0,
        duration: isActive ? 0.32 : 0.18,
        delay: isActive ? 0.24 : 0,
        ease: 'power2.out',
      })
    }
    el.setAttribute('aria-expanded', String(isActive))
  }

  // Initial layout — set BEFORE first paint so panels appear in their right
  // positions immediately, no flash of equal-width tiles.
  useLayoutEffect(() => {
    if (!railRef.current) return
    layoutRef.current = computeLayout(railRef.current.clientWidth)
    applyLayout(false)

    panelRefs.current.forEach((el, i) => {
      if (!el) return
      const isActive = i === 0
      gsap.set(el.querySelector('.panel-icon-box'), { scale: isActive ? 1.06 : 1, rotation: isActive ? 4 : 0 })
      gsap.set(el.querySelector('.panel-icon'), { scale: isActive ? 1.12 : 1, rotation: isActive ? -10 : 0 })
      gsap.set(el.querySelector('.panel-glow'), { opacity: isActive ? 1 : 0 })
      gsap.set(el.querySelector('.panel-strip-title'), { opacity: isActive ? 0 : 1 })
      gsap.set(el.querySelector('.panel-reveal'), { opacity: isActive ? 1 : 0 })
      el.setAttribute('aria-expanded', String(isActive))
    })
  }, [])

  // Recompute layout on container resize (RAF-throttled).
  useEffect(() => {
    if (!railRef.current) return
    let rafId: number | null = null
    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        if (!railRef.current) return
        layoutRef.current = computeLayout(railRef.current.clientWidth)
        applyLayout(false)
      })
    })
    ro.observe(railRef.current)
    return () => {
      ro.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [])

  const setActive = (i: number) => {
    if (activeRef.current === i) return
    const prev = activeRef.current
    activeRef.current = i
    applyLayout(true)
    tweenContent(prev, false)
    tweenContent(i, true)
  }

  return (
    <section
      id="features"
      ref={rootRef}
      className="relative px-6 md:px-16 py-32 md:py-44"
    >
      <div className="mx-auto max-w-[1280px]">
        <h2
          className="feat-title font-display font-semibold text-ink-1 mb-16 md:mb-24"
          style={{
            fontSize: 'clamp(56px, 8vw, 128px)',
            letterSpacing: '-0.04em',
            lineHeight: 1,
          }}
        >
          Features
        </h2>

        {/*
          Desktop accordion. NOT flex / NOT grid — each panel is absolutely
          positioned with explicit x + width controlled by GSAP. This keeps
          layout-thrash isolated to a single panel (via `contain: layout`)
          instead of reflowing the entire rail every frame, which is what
          made flex-grow tweening lag with 9 panels at once.
        */}
        <div
          ref={railRef}
          className="feat-rail hidden lg:block relative w-full"
          style={{ height: RAIL_HEIGHT }}
        >
          {FEATURES.map(({ Icon, title, desc, Mock }, i) => (
            <FeatPanel
              key={title}
              onActivate={() => setActive(i)}
              registerRef={(el) => (panelRefs.current[i] = el)}
              isTheme={i === FEATURES.length - 1}
              label={`${title} — ${desc}`}
            >
              <PanelStrip
                Icon={Icon}
                title={title}
                themeStyle={i === FEATURES.length - 1}
              />
              <PanelExpanded Icon={Icon} title={title} desc={desc} Mock={Mock} />
            </FeatPanel>
          ))}
        </div>

        {/* Mobile/tablet: Settings-style accordion (single-expand, first open). */}
        <div className="lg:hidden">
          <FeaturesAccordion features={FEATURES} />
        </div>
      </div>
    </section>
  )
}

// ============================================================================
// Mobile accordion — single-expand, first item open by default.
// Header tap toggles; chevron rotates; content slides via grid-template-rows.
// Icons spring-in and rows fade-up via IntersectionObserver on first view.
// ============================================================================

function FeaturesAccordion({ features }: { features: Feature[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(0)
  return (
    <div className="flex flex-col gap-2.5">
      {features.map((feature, i) => (
        <AccordionRow
          key={feature.title}
          index={i}
          feature={feature}
          isOpen={activeIndex === i}
          onToggle={() => setActiveIndex(activeIndex === i ? null : i)}
        />
      ))}
    </div>
  )
}

interface AccordionRowProps {
  feature: Feature
  isOpen: boolean
  onToggle: () => void
  index: number
}

function AccordionRow({ feature, isOpen, onToggle, index }: AccordionRowProps) {
  const { Icon, title, desc, Mock } = feature
  const rowRef = useRef<HTMLElement>(null)
  const iconRef = useRef<HTMLSpanElement>(null)

  // Fade-up on first view + icon spring-in. Once-only.
  useEffect(() => {
    const el = rowRef.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.setAttribute('data-visible', 'true')
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.setAttribute('data-visible', 'true')
          iconRef.current?.classList.add('icon-spring-in')
          io.disconnect()
        }
      },
      { threshold: 0.15 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // Stagger fade-up by index, capped at 320ms so the 9th row isn't laggard.
  const staggerDelay = Math.min(index * 60, 320)

  return (
    <article
      ref={rowRef}
      data-open={isOpen ? 'true' : 'false'}
      className="reveal-up feat-accordion glass rounded-2xl overflow-hidden"
      style={{ transitionDelay: `${staggerDelay}ms` }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="tap-glow w-full flex items-center gap-3 px-4 py-4 text-left"
      >
        <span
          ref={iconRef}
          className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.05] border border-white/10"
        >
          <Icon size={18} strokeWidth={1.75} style={{ color: 'var(--accent)' }} />
        </span>
        <h3 className="flex-1 text-[15px] font-semibold tracking-tight text-ink-1 leading-tight">
          {title}
        </h3>
        <ChevronRight
          size={18}
          className="shrink-0 text-ink-3"
          style={{
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
      </button>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: isOpen ? '1fr' : '0fr',
          transition: 'grid-template-rows 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <div className="px-4 pb-5 pt-1">
            <p className="text-[14px] text-ink-2 leading-relaxed mb-4">{desc}</p>
            <div className="flex items-center justify-center">
              <Mock />
            </div>
          </div>
        </div>
      </div>
    </article>
  )
}

function FeatPanel({
  onActivate,
  registerRef,
  children,
  isTheme,
  label,
}: {
  onActivate: () => void
  registerRef: (el: HTMLElement | null) => void
  children: React.ReactNode
  isTheme?: boolean
  label: string
}) {
  return (
    <article
      ref={registerRef}
      onPointerEnter={onActivate}
      onFocus={onActivate}
      tabIndex={0}
      role="group"
      aria-label={label}
      className="feat-panel group glass overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:[box-shadow:inset_0_0_0_2px_var(--accent),0_0_0_4px_var(--accent-soft)]"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        // GSAP sets x (transform: translateX) and width on this element.
        // `contain: layout style paint` makes the browser treat this panel
        // as an independent layout subtree — its width changes don't
        // reflow siblings, only its own children.
        contain: 'layout style paint' as never,
        // Hint the compositor that we'll be transforming this element.
        willChange: 'transform, width',
      }}
      data-theme={isTheme ? '1' : '0'}
    >
      <span
        aria-hidden
        className="panel-glow pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{
          background:
            'radial-gradient(80% 80% at 0% 0%, var(--accent-soft) 0%, transparent 60%)',
          opacity: 0,
        }}
      />
      {children}
    </article>
  )
}

function PanelStrip({
  Icon,
  title,
  themeStyle,
}: {
  Icon: LucideIcon
  title: string
  themeStyle?: boolean
}) {
  return (
    <div className="panel-strip-title absolute inset-0 p-5 flex flex-col items-center justify-between pointer-events-none">
      <span
        className="panel-icon-box inline-flex items-center justify-center w-12 h-12 rounded-2xl will-change-transform shrink-0"
        style={{
          transformOrigin: 'center',
          background: themeStyle
            ? 'linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%)'
            : 'rgba(255,255,255,0.05)',
          border: themeStyle
            ? '1px solid rgba(255,255,255,0.15)'
            : '1px solid rgba(255,255,255,0.10)',
          boxShadow: themeStyle ? '0 8px 24px -8px var(--accent-ring)' : 'none',
        }}
      >
        <Icon
          className="panel-icon will-change-transform"
          size={22}
          strokeWidth={1.75}
          style={{ color: themeStyle ? '#09090b' : 'var(--accent)', transformOrigin: 'center' }}
        />
      </span>
      <p
        className="text-[11px] text-ink-3 uppercase tracking-[2px] text-center leading-tight whitespace-nowrap"
        style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          maxHeight: 240,
        }}
      >
        {title}
      </p>
    </div>
  )
}

function PanelExpanded({
  Icon,
  title,
  desc,
  Mock,
}: {
  Icon: LucideIcon
  title: string
  desc: string
  Mock: ComponentType
}) {
  return (
    <div
      className="panel-reveal absolute inset-y-0 left-0 p-7 flex flex-col gap-5"
      style={{
        opacity: 0,
        // Fixed inner width so text wraps ONCE on mount and stays put as
        // the column animates. The portion that overflows a collapsed panel
        // is clipped invisibly by the parent's overflow:hidden.
        width: REVEAL_INNER_W,
        contain: 'layout style paint' as never,
      }}
    >
      <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/[0.05] border border-white/10">
        <Icon size={22} strokeWidth={1.75} style={{ color: 'var(--accent)' }} />
      </span>

      <div>
        <h3
          className="text-[22px] font-semibold tracking-tight text-ink-1 leading-tight mb-2"
          style={{ textWrap: 'balance' as never }}
        >
          {title}
        </h3>
        <p
          className="text-[14px] text-ink-2 leading-relaxed"
          style={{ textWrap: 'pretty' as never }}
        >
          {desc}
        </p>
      </div>

      <div className="mt-auto flex items-end justify-center pb-1">
        <Mock />
      </div>
    </div>
  )
}
